import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * TEM AUD-1017 · C03 (17-Oct-2026) — Cantidad escrita a mano en el carrito.
 * Horizon solo confirma la cantidad escrita en la casilla al salir del campo (on:blur).
 * Mientras el cursor sigue en la casilla, el importe de la línea, el total y el contador
 * siguen mostrando la cantidad anterior; pero el botón "Pagar" envía el formulario
 * cart-form con updates[] = cantidad escrita, así que el checkout cobra la cantidad nueva.
 * Además, Enter en la casilla enviaba ese formulario y saltaba directo al checkout.
 * Arreglo: confirmar la cantidad escrita tras una pausa breve de escritura y al pulsar
 * Enter (sin enviar el formulario). Al salir del campo no se repite la misma petición.
 * C03b: un valor vacío, 0 o no entero ya no se convierte en 1 en silencio: se restaura la
 * cantidad real del carrito y se avisa en la línea (evento 'tem:quantity-invalid').
 * El componente del carrito usa hasPendingQuantity()/cancelPendingQuantity() antes de Pagar.
 * Original de Horizon 4.1.3: evidencia/originales/assets/component-cart-quantity-selector.js (md5 c7d753e6…).
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #typingTimer;
  /** @type {string | null} Último valor ya confirmado desde la escritura. */
  #committedValue = null;
  /** Se rechazó un valor inválido y el cliente todavía no vio el aviso antes de Pagar. */
  #invalidNotice = false;

  connectedCallback() {
    super.connectedCallback();
    // Delegado en el propio elemento: sobrevive a que el carrito re-renderice la casilla.
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#typingTimer);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusin', this.#onFocusIn);
  }

  /** Cantidad que el servidor puso en la casilla al renderizar (estado real del carrito). */
  get #serverValue() {
    const { quantityInput } = this.refs;
    return quantityInput.getAttribute('value') ?? quantityInput.defaultValue;
  }

  /**
   * ¿El texto de la casilla es una cantidad que se puede confirmar? (entero ≥ mínimo)
   * @param {string} raw
   */
  #isCommittable(raw) {
    const { min } = this.getCurrentValues();
    return /^\d+$/.test(raw.trim()) && parseInt(raw, 10) >= min;
  }

  /** C03b: hay una cantidad escrita que todavía no coincide con el carrito. */
  hasPendingQuantity() {
    const { quantityInput } = this.refs;
    return (
      this.#invalidNotice ||
      this.#typingTimer !== undefined ||
      (quantityInput && quantityInput.value !== this.#serverValue)
    );
  }

  /** C03b: true una sola vez si se rechazó un valor inválido justo antes de Pagar. */
  consumeInvalidNotice() {
    const had = this.#invalidNotice;
    this.#invalidNotice = false;
    return had;
  }

  /** C03b: el carrito confirma él mismo antes de Pagar; cancelar el temporizador propio. */
  cancelPendingQuantity() {
    clearTimeout(this.#typingTimer);
    this.#typingTimer = undefined;
  }

  /** @param {Event} event */
  #isQuantityInput(event) {
    return event.target instanceof HTMLInputElement && event.target === this.refs.quantityInput;
  }

  /** @param {Event} event */
  #onFocusIn = (event) => {
    if (this.#isQuantityInput(event)) this.#committedValue = null;
  };

  /** @param {Event} event */
  #onInput = (event) => {
    if (!this.#isQuantityInput(event)) return;
    this.cancelPendingQuantity();
    this.#invalidNotice = false;
    // Casilla vacía, a medio escribir, 0 o con decimales: no confirmar todavía.
    if (!this.#isCommittable(this.refs.quantityInput.value)) return;
    this.#typingTimer = setTimeout(() => {
      this.#typingTimer = undefined;
      this.#commitTypedValue();
    }, 700);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#isQuantityInput(event) || event.key !== 'Enter') return;
    // Enter no debe enviar cart-form (eso lleva directo al checkout).
    event.preventDefault();
    this.cancelPendingQuantity();
    if (!this.#isCommittable(this.refs.quantityInput.value)) {
      this.#rejectInvalid();
      return;
    }
    this.#commitTypedValue();
  };

  #commitTypedValue() {
    const { quantityInput } = this.refs;
    if (!quantityInput || !this.#isCommittable(quantityInput.value)) return;
    super.setQuantity(/** @type {Event} */ (/** @type {unknown} */ ({ target: quantityInput, preventDefault() {} })));
    this.#committedValue = quantityInput.value;
  }

  /** Restaura la cantidad real y avisa al carrito para que lo muestre en la línea. */
  #rejectInvalid() {
    const { quantityInput } = this.refs;
    const { min } = this.getCurrentValues();
    const raw = quantityInput.value.trim();
    quantityInput.value = this.#serverValue;
    this.#invalidNotice = true;
    this.updateButtonStates();
    const message =
      /^\d+$/.test(raw) && parseInt(raw, 10) < min
        ? `La cantidad mínima es ${min}. Para quitar el producto usá Eliminar.`
        : 'Ingresá una cantidad válida (un número entero).';
    this.dispatchEvent(
      new CustomEvent('tem:quantity-invalid', {
        bubbles: true,
        detail: { line: Number(quantityInput.dataset.cartLine) || undefined, message },
      })
    );
  }

  /**
   * Al salir del campo: si la cantidad ya se confirmó mientras se escribía, no repetir la petición.
   * Si el valor no es válido, restaurar en vez de convertirlo en el mínimo.
   * @param {Event} event
   */
  setQuantity(event) {
    this.cancelPendingQuantity();
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.value === this.#serverValue) return; // Sin cambios: nada que enviar.
    if (!this.#isCommittable(event.target.value)) {
      this.#rejectInvalid();
      return;
    }
    if (this.#committedValue !== null && event.target.value === this.#committedValue) return;
    super.setQuantity(event);
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
